// Relative path: src/components/BarGraph.js

import React, { useState, useRef, useEffect } from 'react';

const BarGraph = ({ 
  title, 
  subtitle, 
  data = [], 
  minValue = 0, 
  maxValue = 5, 
  showScale = true,
  animationDelay = 0.5,
  className = ""
}) => {
  const [animated, setAnimated] = useState(false);
  const graphRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setAnimated(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (graphRef.current) {
      observer.observe(graphRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Calculate the actual max value based on data
  const getActualMaxValue = () => {
    if (data.length === 0) return maxValue;
    
    const dataMax = Math.max(...data.map(item => item.value));
    // Find the next 0.5 increment that's >= dataMax
    const nextHalfIncrement = Math.ceil(dataMax * 2) / 2;
    // Don't exceed the original maxValue parameter
    return Math.min(nextHalfIncrement, maxValue);
  };

  const actualMaxValue = getActualMaxValue();

  // Calculate percentage for each bar based on min/actualMax values
  const getBarWidth = (value) => {
    const range = actualMaxValue - minValue;
    const adjustedValue = value - minValue;
    return Math.max(0, Math.min(100, (adjustedValue / range) * 100));
  };

  // Generate scale labels with 0.5 point gaps
  const getScaleLabels = () => {
    const labels = [];
    const range = actualMaxValue - minValue;
    const stepValue = 0.5;
    const stepCount = Math.ceil(range / stepValue);
    
    for (let i = 0; i <= stepCount; i++) {
      const value = minValue + (stepValue * i);
      if (value <= actualMaxValue) {
        const position = (value - minValue) / range * 100;
        labels.push({
          value: value.toFixed(1),
          position: Math.min(position, 100) // Ensure we don't exceed 100%
        });
      }
    }
    return labels;
  };

  const scaleLabels = getScaleLabels();

  return (
    <div 
      ref={graphRef} 
      className={` rounded-lg pt-5 pb-12 px-6 md:px-16 ${className}`}
    >
      {title && (
        <h3 className="text-start text-lg font-semibold text-gray-700 mb-0">
          {title}
        </h3>
      )}
      {subtitle && (
        <div className="text-start text-sm text-gray-500 mb-4">
          {subtitle}
        </div>
      )}
      
      <div className="relative" style={{ minHeight: '130px' }}>
        {/* Scale lines and labels */}
        {showScale && (
          <div className="absolute inset-0 pointer-events-none">
            {scaleLabels.map((label, index) => (
              <div
                key={index}
                className="absolute h-full"
                style={{ left: `${label.position}%` }}
              >
                {/* Vertical line */}
                <div 
                  className="w-0.5 h-full bg-gray-200"
                  style={{ 
                    backgroundColor: index === 0 || index === scaleLabels.length - 1 
                      ? 'rgba(229, 231, 235, 1)' 
                      : 'rgba(229, 231, 235, 1)' 
                  }}
                />
                {/* Scale label */}
                <div 
                  className="absolute text-xs text-gray-500 text-center"
                  style={{ 
                    bottom: '-20px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: '40px'
                  }}
                >
                  {label.value}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Bars container */}
        <div className="relative z-10 pt-4 pb-6">
          {data.map((item, index) => (
            <div key={index} className="mb-4">
              <div 
                className="h-8 rounded-r flex items-center relative overflow-hidden"
                style={{
                  width: animated ? `${getBarWidth(item.value)}%` : '0%',
                  backgroundColor: item.color || '#3cd3ad',
                  transition: `width 1s ease ${animationDelay + (index * 0.2)}s`,
                  minWidth: animated ? '120px' : '0px' // Ensure minimum width for label visibility
                }}
              >
                {/* Bar label inside */}
                <span 
                  className="text-white text-md font-medium px-2 whitespace-nowrap"
                  style={{
                    opacity: animated ? 1 : 0,
                    transition: `opacity 0.5s ease ${animationDelay + (index * 0.2) + 0.5}s`
                  }}
                >
                  {item.name}
                </span>
                
                {/* Value label at the end */}
                <span 
                  className="absolute text-sm text-gray-600 font-medium whitespace-nowrap"
                  style={{
                    left: '100%',
                    marginLeft: '8px',
                    opacity: animated ? 1 : 0,
                    transition: `opacity 0.5s ease ${animationDelay + (index * 0.2) + 0.7}s`
                  }}
                >
                  {item.value.toFixed(1)}/5
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      <style jsx>{`
        @keyframes slideInFromLeft {
          from {
            width: 0%;
            opacity: 0;
          }
          to {
            width: var(--target-width);
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
};

export default BarGraph;